<template>
  <div class="orders">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        :show-close-button="false"
        class="orders orders-modal !fixed !inset-0 !top-0 !left-0 !w-screen !h-screen !max-w-none !rounded-none !translate-x-0 !translate-y-0 !p-0 !border-0 !gap-0 !grid-rows-[auto_auto_1fr] overflow-y-auto overflow-x-hidden [&>*]:min-w-0"
        @open-auto-focus.prevent
        data-cy="orders-modal"
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("orders.orders") }}
          </SDialogTitle>
        </SDialogHeader>
        <STabs v-model="activeTab" class="contents">
          <div class="customized-header d-flex flex-row fl-s-b">
            <div class="header-title d-flex fl-col fl-j-c">
              <div v-if="!isOrderSelected">
                <p>{{ $t(activeTabTitleKey) }}</p>
              </div>

              <div v-else @click="goBack" class="d-flex flex-row fl-s-b">
                <div class="go-back-icon">
                  <SvgIcon
                    :iconContent="leftArrowIconContent"
                    :iconAlt="leftArrowIconAlt"
                    :iconStyle="leftArrowIconStyle"
                  />
                </div>

                <div class="d-flex fl-col fl-j-c">
                  <p>{{ $t("button.back") }}</p>
                </div>
              </div>
            </div>

            <div class="close-button d-flex fl-col fl-j-c" @click="close">
              <SvgIcon
                :iconContent="closeIconContent"
                :iconAlt="closeIconAlt"
                :iconStyle="closeIconStyle"
              />
            </div>
          </div>

          <div class="customized-body">
            <STabsList
              v-if="!isOrderSelected"
              class="orders-tabs-list"
              data-cy="orders-tabs"
            >
              <STabsTrigger value="orders" data-cy="orders-tab-trigger">
                {{ $t("orders.orders") }}
              </STabsTrigger>
              <STabsTrigger value="attendees" data-cy="attendees-tab-trigger">
                {{ $t("headers.all-attendees") }}
              </STabsTrigger>
              <STabsTrigger
                value="memberships"
                data-cy="memberships-tab-trigger"
              >
                {{ $t("headers.all-memberships") }}
              </STabsTrigger>
              <STabsTrigger value="gift-cards" data-cy="gift-cards-tab-trigger">
                {{ $t("headers.all-gift-cards") }}
              </STabsTrigger>
            </STabsList>

            <STabsContent
              value="orders"
              class="orders-tab-content"
              data-cy="orders-tab-content"
            >
              <div v-if="!isOrderSelected" style="padding: 20px">
                <OrdersSearchTable
                  v-if="groupId"
                  :key="groupId"
                  :group-id="groupId"
                  :show-teams-column="showTeamsColumn"
                />
              </div>

              <div
                v-if="isOrderSelected"
                class="order-details d-flex fl-col fl-j-c"
              >
                <div
                  v-show="!isOrderViewLoaded"
                  class="progressbar-wrapper d-flex fl-col fl-s-b"
                >
                  <div class="img-wrapper">
                    <img
                      src="@/assets/images/icons/girl-running.png"
                      alt="Logo"
                    />
                  </div>

                  <div>
                    <p class="loading-text">{{ $t("common.loading") }}</p>
                  </div>

                  <div>
                    <el-progress
                      :percentage="70"
                      :indeterminate="true"
                      stroke-width="26"
                      :show-text="false"
                    />
                  </div>

                  <div class="one-sec-text">
                    <p>{{ $t("common.one-sec") }}</p>
                  </div>
                </div>

                <iframe
                  v-show="isOrderViewLoaded"
                  :src="frameSource"
                  frameborder="0"
                  width="100%"
                  :height="frameHeight"
                />
              </div>
            </STabsContent>

            <STabsContent
              value="attendees"
              class="attendees-tab-content"
              style="padding: 20px"
              data-cy="attendees-tab-content"
            >
              <AttendeesSearchTable
                v-if="groupId"
                :key="`attendees-${groupId}`"
                :group-id="groupId"
              />
            </STabsContent>

            <STabsContent
              value="memberships"
              class="memberships-tab-content"
              style="padding: 20px"
              data-cy="memberships-tab-content"
            >
              <MembershipsSearchTable
                v-if="groupId"
                :key="`memberships-${groupId}`"
                :group-id="groupId"
              />
            </STabsContent>

            <STabsContent
              value="gift-cards"
              class="gift-cards-tab-content"
              style="padding: 20px"
              data-cy="gift-cards-tab-content"
            >
              <GiftCardsSearchTable
                v-if="groupId"
                :key="`gift-cards-${groupId}`"
                :group-id="groupId"
              />
            </STabsContent>
          </div>
        </STabs>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";

import OrdersSearchTable from "./OrdersSearchTable.vue";
import AttendeesSearchTable from "@/components/modal/attendees/AttendeesSearchTable.vue";
import MembershipsSearchTable from "@/components/modal/memberships/MembershipsSearchTable.vue";
import GiftCardsSearchTable from "@/components/modal/gift-cards/GiftCardsSearchTable.vue";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { setOrderManagementTokens } from "@/utils/helpers/auth";
import { B2B_IFRAME_URL } from "@/constants/env";
import { LogService } from "@/services/logging";
import { getElementsByQuery } from "@/utils/helpers/dom";
import closeIcon from "@/assets/images/svg/close.svg";
import leftArrowIcon from "@/assets/images/svg/left-arrow.svg";
import { close } from "@/utils/vue-helpers/orders";
import type { IParsedOrder } from "@/interfaces/store/IOrder";
import {
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  STabs,
  STabsList,
  STabsTrigger,
  STabsContent,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "Orders",
  components: {
    OrdersSearchTable,
    AttendeesSearchTable,
    MembershipsSearchTable,
    GiftCardsSearchTable,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    STabs,
    STabsList,
    STabsTrigger,
    STabsContent,
  },
  setup() {
    const closeIconStyle =
      "width: 30px; height: 30px; filter: contrast(170%) invert(100%);";
    const closeIconContent = closeIcon;
    const closeIconAlt = "Close";

    const leftArrowIconStyle =
      "width: 30px; height: 30px; filter: contrast(170%) invert(100%);";
    const leftArrowIconContent = leftArrowIcon;
    const leftArrowIconAlt = "Back";

    const dialogVisible = dialogVisibleForStatus(ModalStatus.Orders);

    const activeTab = ref("orders");

    /**
     * Header title i18n key for the currently active tab, so the teal header
     * reflects which table is shown (Orders / Attendees / Memberships / Gift
     * cards) instead of a static label.
     *
     * @returns {string} The i18n key for the active tab's title.
     */
    const activeTabTitleKey = computed(() => {
      const titles: Record<string, string> = {
        orders: "orders.orders",
        attendees: "headers.all-attendees",
        memberships: "headers.all-memberships",
        "gift-cards": "headers.all-gift-cards",
      };

      return titles[activeTab.value] ?? "orders.orders";
    });

    const isOrderSelected = ref(false);
    const isOrderViewLoaded = ref(false);
    const selectedOrderId = ref("");
    const selectedBookingRef = ref("");
    const frameSource = ref("");
    const frameHeight = ref("");

    setOrderManagementTokens(".smeetz.com");

    const groupId = computed(() => UserModule.getUserGroupId);

    /**
     * Show the Teams column only for Fort Boyard and our testing groups.
     *
     * @returns {boolean} True if the Teams column should be visible.
     */
    const showTeamsColumn = computed(() => {
      const id = POSConfigModule.groupId;

      if (!id) {
        return false;
      }

      const allowed = [
        1,
        16383, // DEV
        16342, // TEST
        19578,
        19581,
        19582,
        19583,
        19584,
        19585,
        19586,
        19547, // Fort Boyard
      ];

      return allowed.includes(id);
    });

    /**
     * Return from the iframe-view back to the orders table view.
     *
     * @returns {boolean} Sentinel.
     */
    function goBack(): boolean {
      isOrderSelected.value = false;

      return true;
    }

    return {
      dialogVisible,
      activeTab,
      activeTabTitleKey,
      isOrderSelected,
      isOrderViewLoaded,
      selectedOrderId,
      selectedBookingRef,
      frameSource,
      frameHeight,
      groupId,
      showTeamsColumn,
      closeIconStyle,
      closeIconContent,
      closeIconAlt,
      leftArrowIconStyle,
      leftArrowIconContent,
      leftArrowIconAlt,
      goBack,
      close,
    };
  },

  methods: {
    /**
     * Open the iframe view for the given order — sets the iframe URL,
     * collapses the modal header, and waits for the LOADED postMessage from
     * the embedded B2B app.
     *
     * @param {IParsedOrder} row - Order whose detail view we open.
     * @returns {boolean} Sentinel.
     */
    selectOrder(row: IParsedOrder): boolean {
      this.initListener();
      this.selectedOrderId = row.id;
      this.selectedBookingRef = row.reference;
      this.isOrderSelected = true;

      const bookingViewUrl =
        B2B_IFRAME_URL +
        `${this.selectedBookingRef}/overview?groupId=${this.groupId}&bookingId=${this.selectedOrderId}`;

      LogService.info(`Loading B2B app order view`, {
        orderId: row.id,
        orderRef: row.reference,
        bookingViewUrl,
      });

      this.frameSource = bookingViewUrl;
      const windowHeight = window.innerHeight;
      this.frameHeight = `${windowHeight - 20}px`;

      const dialogHeader = getElementsByQuery(
        ".orders > .el-overlay > .el-overlay-dialog > .el-dialog > .el-dialog__header"
      )[0];

      if (dialogHeader) {
        dialogHeader.classList.add("nosize");
      }

      return true;
    },

    /**
     * Subscribe once to the iframe's `LOADED` postMessage. Fired by selectOrder.
     *
     * @returns {boolean} Sentinel.
     */
    initListener(): boolean {
      window.addEventListener(
        "message",
        (event) => {
          if (!event.data) {
            return;
          }

          try {
            const data = JSON.parse(event.data);

            if (data.page === "order" && data.status === "LOADED") {
              this.isOrderViewLoaded = true;
            }
          } catch (err) {
            throw new Error("Error when parsing event.data: " + event.data);
          }
        },
        { once: true }
      );

      return true;
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
$font-family: Arial, sans-serif, Arial;

.orders-modal {
  .customized-header {
    padding-left: 30px;
    padding-right: 30px;
    background-color: #004c53;
    height: 45px;
    color: white;

    .header-title p {
      font-size: 18px;
      font-weight: 700;
    }

    .go-back-icon {
      margin-right: 15px;
    }
  }

  .nosize {
    padding: 0px;
    margin: 0px;
  }

  // Native STabs styling (design system). Only position it — no colour /
  // background overrides — so it renders as the DS segmented control on the
  // light body background.
  .orders-tabs-list {
    margin: 20px 20px 0;
  }

  // `display: contents` on <STabs> drops these out of SDialogContent's direct
  // children, so the dialog's `[&>*]:min-w-0` no longer reaches them. Re-apply
  // min-width:0 here, otherwise the (wide) ticket table dictates the width and
  // pushes the table toolbar / settings / pagination past the viewport edge.
  .customized-header,
  .customized-body {
    min-width: 0;
  }

  .customized-body {
    padding-left: 20px;
    padding-right: 20px;
    height: 80%;

    .orders-tab-content,
    .attendees-tab-content,
    .memberships-tab-content,
    .gift-cards-tab-content {
      height: 100%;
      min-width: 0;
    }

    .order-details {
      height: 100%;

      .progressbar-wrapper {
        height: 125px;
        width: 40%;
        margin-left: auto;
        margin-right: auto;
        margin-top: 40px;
        margin-bottom: 40px;
        text-align: center;
        color: #004c53;

        .img-wrapper img {
          width: 200px;
          height: 137px;
        }

        .loading-text {
          font-weight: 600;
          font-size: 17px;
          line-height: 19px;
          margin-bottom: 10px;
        }

        .one-sec-text {
          font-weight: 400;
          font-size: 14px;
          line-height: 16px;
          margin-top: 15px;
        }

        .el-progress-bar {
          .el-progress-bar__outer {
            height: 26px;
            border: 2px solid #004c53;
            background-color: transparent;

            .el-progress-bar__inner {
              height: 20px;
              top: 1px;
            }
          }
        }
      }
    }
  }
}

.tickets-list-popup {
  [data-slot="dialog-header"] {
    display: block;
  }

  padding: 20px;
}
</style>
